import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Space, Typography, message, Alert, Empty, Tooltip } from 'antd';
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import settlementApi from '../../../../api/settlementApi';

const { Text } = Typography;

/**
 * 批次未识别费用清单 Tab — 结算账单数据可信化(录入员侧)S1 / T16
 *
 * 数据来源:GET /api/v3/settlement/batches/{batchId}/unrecognized-costs
 * 业务背景:
 *   账单上的费用类型字符串(如手抖打成 "Mystery Fee")无法被系统标准化时,
 *   系统会兜底归类为 OTHERS_FEE(金额不丢),同时把原始写法保留。
 *   录入员在批次详情里看到本 Tab → 决策"加映射"或"留在其他费用"。
 *
 * 已知边界(展示于页面顶部 Alert):
 *   * 仅覆盖 DIRECT 类型行项目(`included_in_batches` 数组记录)
 *   * ALLOCATABLE 类型的"未识别"需从 S4 反查页(T17 完成后)进入下钻看到
 */
const UnrecognizedCostsTab = ({ batchId }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ items: [], total: 0, batch_name: '' });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });

    const loadData = useCallback(
        async (page, pageSize) => {
            if (!batchId) return;
            setLoading(true);
            try {
                const res = await settlementApi.dataQuality.getBatchUnrecognizedCosts(
                    batchId,
                    { page, pageSize }
                );
                setData(res.data);
            } catch (err) {
                console.error('加载未识别费用清单失败:', err);
                message.error(
                    '加载未识别费用清单失败: ' + (err.response?.data?.detail || err.message)
                );
            } finally {
                setLoading(false);
            }
        },
        [batchId]
    );

    useEffect(() => {
        loadData(pagination.current, pagination.pageSize);
        // 故意只跟 current/pageSize 标量,避免 pagination 对象引用每次新建造成的死循环
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData, pagination.current, pagination.pageSize]);

    const handleTableChange = (newPagination) => {
        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const columns = [
        {
            title: (
                <Space>
                    账单上的原始写法
                    <Tooltip title="账单上原本写的费用类型字符串,系统未能匹配到 22 个标准分类中的任何一项。">
                        <InfoCircleOutlined style={{ color: '#1677ff' }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'unrecognized_original_text',
            key: 'unrecognized_original_text',
            width: 200,
            render: (text) => (
                <Tag color="orange" icon={<WarningOutlined />} style={{ fontFamily: 'monospace' }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: '当前归类',
            dataIndex: 'current_cost_type',
            key: 'current_cost_type',
            width: 130,
            align: 'center',
            render: (v) => (
                <Tag color="default">{v}</Tag>
            ),
        },
        {
            title: '金额',
            dataIndex: 'base_total_amount',
            key: 'base_total_amount',
            width: 140,
            align: 'right',
            render: (v, record) => (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Text strong>
                        ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    {record.original_currency && record.original_currency !== record.base_currency && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.original_currency}{' '}
                            {Number(record.original_total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            width: 220,
            ellipsis: { showTitle: true },
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: '航班',
            key: 'flight',
            width: 180,
            render: (_, record) =>
                record.flight_number ? (
                    <Space size={4} direction="vertical">
                        <Text strong>{record.flight_number}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.departure_airport} → {record.arrival_airport}
                        </Text>
                    </Space>
                ) : (
                    <Text type="secondary">未关联航班</Text>
                ),
        },
        {
            title: '航班日期',
            dataIndex: 'flight_date',
            key: 'flight_date',
            width: 110,
            render: (v) => (v ? v.split('T')[0] : '—'),
        },
        {
            title: '账单号',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            width: 150,
            ellipsis: true,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: '分摊类型',
            dataIndex: 'allocation_type',
            key: 'allocation_type',
            width: 100,
            align: 'center',
            render: (v) => <Tag color={v === 'DIRECT' ? 'blue' : 'purple'}>{v}</Tag>,
        },
    ];

    return (
        <div>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={'什么是「未识别费用写法」?'}
                description={
                    <div>
                        <div>
                            账单上的费用类型(比如 <Text code>Mystery Fee</Text>、<Text code>燃油2</Text>)
                            未能被系统映射到 22 个标准分类时,会被兜底归到{' '}
                            <Tag color="default" style={{ marginInline: 4 }}>OTHERS_FEE</Tag>
                            ,<Text strong>金额不丢</Text>,但原始写法保留在此清单。
                        </div>
                        <div style={{ marginTop: 4 }}>
                            录入员可以:① 让系统加一条「未来识别这种写法」的映射(去归类), 或者
                            ② 接受当前归类不动作。详见{' '}
                            <Text type="secondary">「数据异常工作台」 → 规则 3 「出现新的费用写法」</Text>。
                        </div>
                        <div style={{ marginTop: 4, color: '#888' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                注:本清单只覆盖直接成本(DIRECT)。分摊类(ALLOCATABLE)
                                费用的未识别情况需从航段费用反查页按航班+类型进入下钻查看。
                            </Text>
                        </div>
                    </div>
                }
            />

            {data.total === 0 && !loading ? (
                <Empty description={'本批次未发现「未识别费用写法」,数据质量良好'} />
            ) : (
                <Table
                    rowKey="invoice_line_item_id"
                    columns={columns}
                    dataSource={data.items}
                    loading={loading}
                    size="small"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: data.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        showTotal: (total) => `共 ${total} 条未识别费用`,
                    }}
                    onChange={handleTableChange}
                />
            )}
        </div>
    );
};

export default UnrecognizedCostsTab;
